import * as React from 'react';
import { ReactElement, useState, useRef } from 'react';
import { 
    Card,
    Checkbox,
    FormControlLabel,
    Typography,
    Button,
    DialogTitle,
    DialogContent,
    DialogActions,
    Dialog,
    FormControl,
    Select,
    InputLabel,
    MenuItem,
    TextField,
    CircularProgress
} from '@mui/material';
import Paper, { PaperProps } from '@mui/material/Paper';
import Draggable from 'react-draggable';
import RemoveIcon from '@mui/icons-material/Remove';
import HttpClient from '../../../providers/HttpClient';
import ApiResources from '../../../providers/ApiResources';
import { convertFileToBase64 } from '../../../common/UtilFunctions';
import { useNotify } from "react-admin";

function PaperComponent(props: PaperProps) {
    const draggableRef = useRef(null);

    return (
        <Draggable
            nodeRef={draggableRef}
            handle="#draggable-dialog-title"
            cancel={'[class*="MuiDialogContent-root"]'}
        >
            <Paper ref={draggableRef} {...props} />
        </Draggable>
    );
}

export const ImportSettings = (props: ImportSettingsProps) => {
    const { 
        name, 
        sheet, 
        columns,
        open, 
        onClose, 
        availableAttributes, 
        assignAttributes,
        imageFiles,
        logger
    } = props;

    const notify = useNotify();
    const [isConfirmed, setIsConfirmed] = useState(false);
    const [isForcedCreation, setIsForcedCreation] = useState(false);
    const [imageOverwrite, setImageOverwrite] = useState(false);
    const forcedCreationAttributes: any = [ 
        'type',
        'attribute_set',
    ];
    const [importOffset, setImportOffset] = useState<any>({from: '', to: ''});
    const [inImportProcess, setInImportProcess] = useState(false);
    const [importTotalRows, setImportTotalRows] = useState(0);
    const [importCounter, setImportCounter] = useState(0);
    const isStopRequested = useRef(false);

    const validateOffsetInput = (e) => {
        const { key, keyCode } = e;

        if (!/^[0-9]+$/.test(key) && ![8,9,17,37,39,67,86].includes(keyCode)) {
          e.preventDefault();
        }
    }

    function checkOffsetInputs() {
        if (parseInt(importOffset?.from) > parseInt(importOffset?.to) && importOffset?.from !== '' && importOffset?.to !== '') {
            return false;
        }

        return true;
    }

    function checkCreateProductAttributes() {
        if (!isForcedCreation) {
            return true;
        }

        let attributesCheck = true;

        forcedCreationAttributes.forEach((creationAttribute)=>{
            const availableAttribute = availableAttributes.filter((available) => available.code == creationAttribute);

            if (availableAttribute.length === 0) {
                attributesCheck = false;
            }
        });

        return attributesCheck;
    }

    const handleCreateProduct = (e) => {
        const { value } = e.target;

        setIsForcedCreation(value === 'create' ? true : false);
    };

    const handleImageOverwrite = (e) => {
        const { value } = e.target;

        setImageOverwrite(value === 'overwrite' ? true : false);
    };

    const handleConfirm = (e) => {
        const { checked } = e.target;

        setIsConfirmed(checked || false);
    }

    const handleOffest = (e) => {
        let { name, value } = e.target;

        setImportOffset({...importOffset, [name]: value});
    }

    const buildImportRequest = async (row) => {
        let requestData = {
            settings: {
                assign: assignAttributes,
                create: isForcedCreation,
                image_overwrite: imageOverwrite
            },
            data: {}
        };

        const sheetData = sheet[row];

        for (const attribute of availableAttributes) {
            if (attribute.primary === 'yes') {
                requestData.settings['primary'] = attribute.code;
            }

            const value = sheetData[attribute.name];

            if (value !== null && value !== '') {
                if (attribute.name === 'Image') {

                    if (isImageUrl(value)) {
                        let fileName = decodeURIComponent(new URL(value).pathname.split('/').pop());

                        requestData.data[attribute.code] = {name: fileName, url: value};

                        continue;
                    } else {
                        const files = imageFiles.filter((file) => file.name == value);

                        if (files.length === 0) {
                            continue;
                        }

                        await convertFileToBase64(files[0]).then((results) => { 
                            requestData.data[attribute.code] = {name: value, data: results};
                        }).catch((error) => {
                            console.log(error);
                        });

                        continue;
                    }
                }

                requestData.data[attribute.code] = value;                 
            }
        }

        return requestData;
    }

    const handleStart = async (e) => {
        if (!checkOffsetInputs()) {
            return;
        }

        if (!checkCreateProductAttributes()) {
            alert('Please add sheet data for ('+forcedCreationAttributes.join(', ')+') attributes');
            return;
        }

        setInImportProcess(true);
        setImportCounter(0);
        isStopRequested.current = false;

        let from = parseInt(importOffset.from || 2);
        from = from < 2 ? 2 : from;
        let to = parseInt(importOffset.to || sheet.length);
        to = to < 2 ? 2 : to;

        setImportTotalRows(to-from+1);

        for (let i = from; i <= to; i++) {
            if (isStopRequested.current) {
                setInImportProcess(false);

                break;
            }

            // matching excel row by subtracting by 2
            let requestData = {};
            await buildImportRequest(i-2).then((data) => {
                requestData = data;
            });

            await HttpClient(ApiResources.postImporter(), {
                method: 'POST',
                body: JSON.stringify(requestData)
            }).then(({ json }) => {
                logger(json);

                return;
            }).catch(error => {
                notify(error.message, { type: 'error' });
            });

            setImportCounter((current)=>{
                return current+1;
            });

            if (i == to) {
               setInImportProcess(false); 
            }
        }  
    }

    const handleStop = (e) => {
        isStopRequested.current = true;
        setInImportProcess(false);
    }

    const isImageUrl = (url) => {
        try {
            const parsed = new URL(url);
            return /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(parsed.pathname);
        } catch {
            return false;
        }
    }

    return (
        <React.Fragment>
            <Dialog
                open={open}
                PaperComponent={PaperComponent}
                aria-labelledby="draggable-dialog-title"
                sx={{'.MuiDialog-container .MuiPaper-root': {boxShadow: '0px 5px 15px -2px rgb(0 0 0 / 20%)', pointerEvents: 'auto'}, pointerEvents: 'none' }}
                hideBackdrop
                disableEnforceFocus
            >
                <DialogTitle style={{ cursor: 'move', borderBottom: '2px solid', margin: '16px 24px', padding: '0px'}} id="draggable-dialog-title">
                      Import Setttings
                </DialogTitle>
                <DialogContent>
                        <Typography variant="subtitle2" sx={{ fontSize: '16px', paddingBottom: '10px' }}>Row Offset:</Typography>
                        <TextField 
                            name="from"
                            type="number" 
                            InputProps={{ inputProps: {min: 2} }} 
                            label="From"
                            value={importOffset?.from}
                            variant="outlined" 
                            sx={{width: 120}}
                            disabled={inImportProcess}
                            onChange={handleOffest}
                            onKeyDown={validateOffsetInput}
                        />
                        <RemoveIcon sx={{marginTop: '15px'}}/>
                        <TextField 
                            name="to"
                            type="number" 
                            InputProps={{ inputProps: {min: 2} }}
                            label="To" 
                            value={importOffset?.to}
                            variant="outlined" 
                            sx={{width: 120}}
                            disabled={inImportProcess}
                            onChange={handleOffest}
                            onKeyDown={validateOffsetInput}
                        />
                        { !checkOffsetInputs() &&
                            <Typography variant="body2" color="error">From offset must be smaller number.</Typography>
                        }

                        <Typography variant="subtitle2" sx={{ fontSize: '16px', marginTop: '15px' }}>Not Found:</Typography>
                        <Typography variant="body2" sx={{ paddingBottom: '5px' }}>If Primary key value is not found. Skip or Create new?</Typography>
                        
                        <FormControl sx={{ m: 1, minWidth: 100, marginLeft: 0 }} variant="outlined">
                            <Select
                                variant="outlined"
                                value={isForcedCreation ? 'create' : 'skip'}
                                onChange={handleCreateProduct}
                                disabled={inImportProcess}
                            >
                                <MenuItem key="skip" value="skip">Skip</MenuItem>
                                <MenuItem key="create" value="create">Create</MenuItem>
                            </Select>
                        </FormControl>
                        { isForcedCreation &&
                            <Typography variant="body2" color="error">To create a product, listed attributes must be specified ({forcedCreationAttributes.join(', ')}).</Typography>
                        }

                        { columns.includes('Image') && 
                        <>
                        <Typography variant="subtitle2" sx={{ fontSize: '16px', marginTop: '15px' }}>Images:</Typography>
                        <Typography variant="body2" sx={{ paddingBottom: '5px' }}>If same image name is already imported. Skip or Overwrite?</Typography>
                        
                        <FormControl sx={{ m: 1, minWidth: 100, marginLeft: 0 }} variant="outlined">
                            <Select
                                variant="outlined"
                                value={imageOverwrite ? 'overwrite' : 'skip'}
                                onChange={handleImageOverwrite}
                                disabled={inImportProcess}
                            >
                                <MenuItem key="skip" value="skip">Skip</MenuItem>
                                <MenuItem key="overwrite" value="overwrite">Overwrite</MenuItem>
                            </Select>
                        </FormControl>
                        </>
                        }
                </DialogContent>
                <DialogActions
                    sx={{ justifyContent: 'space-between', padding: '16px 24px', flexWrap: 'wrap' }}
                >     
                    <FormControlLabel 
                        sx={{ flex: '0 0 100%' }}
                        control={
                            <Checkbox 
                                checked={isConfirmed}
                                onClick={handleConfirm}
                                disabled={inImportProcess}
                            />
                        } 
                        label={"I confirm I want to start import process for "+name} 
                    />
                    <div style={{ display: 'flex' }}>
                        <Button variant="contained" onClick={handleStart} disabled={!isConfirmed || inImportProcess}>Start Import</Button>

                        { inImportProcess && 
                            <React.Fragment>
                                <CircularProgress size={36} sx={{ marginLeft: '15px' }}/>
                                <Typography variant="body2" sx={{ fontSize: '16px', fontWeight: 'bold', margin: '8px' }}>
                                    {importCounter}/{importTotalRows}
                                </Typography>

                                <Button color="error" onClick={handleStop}>Stop</Button>
                            </React.Fragment>
                        }
                    </div>

                    <Button variant="outlined" onClick={onClose} disabled={inImportProcess}>
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    )
}


export interface ImportSettingsProps {
    name: string,
    sheet: any[],
    columns: any[],
    open: boolean,
    onClose: React.MouseEventHandler<HTMLButtonElement>,
    availableAttributes: any[],
    assignAttributes: string[],
    imageFiles: any[],
    logger: (data: string) => void
}